import { WspDistrictAgencyId } from "./wsp";
// This type is for use throughout the code where the "ID" could be a value from more than one type.
// This will also make it convenient if the agency IDs are every replaced by actual IDs instead of
// the current AgencyCode values.
export type AgencyId = AgencyCode | WspDistrictAgencyId;

export interface Agency {
  id: AgencyCode;
  jogId: JogId;
  category: AgencyCategory;
  name: string;
  shortName: string;
  isFilter: boolean;
  isUserAgency: boolean;
}

export interface Jog {
  id: JogId;    
  agencies: Agency[];
  name: string;
  shortName: string;  
  isFilter: boolean;
  isUserJog: boolean;
  isGroupOnly: boolean;
}

export enum AgencyCategory {
  City = "City",
  DOT = "Transportation",
  EmergencyMgmt = "Emergency Mgmt",
  Fire = "Fire",
  LawEnforcement = "Law Enforcement",
  PublicTransit = "Transit",
  PublicUtilities = "Public Utilities",
  PublicWorks = "Public Works",  
  Other = "Other",
}

export enum JogId {
  // Not actual JOG  
  LegacyAgencyGroup = "Legacy", // Supports existing list of user agencies, before switching to use "Agencies" array.
  Wsp = "WSPJog", // No events associated with this ID. Just used to group all Districts for dispatch event filter group.
  Other = "00",  // "No JOG"

  // Actual JOG or Group
  SeattleArea = "01", // "SEA"
  KingCountyNortheast = "02", // "NEJOG"
  KingCountySouth = "03", // "South JOG"
  Tacoma = "04", // "TAC"
  JBLM = "05",
  SnohomishCounty = "06",
  KitsapPeninsula = "07",
  ClarkCounty = "08",
  LowerColumbiaRiver = "11",
  NorthwestWashington = "17", // Includes SkagitCounty & WhatcomCounty.
  
  // Group, not actual JOG. No incidents or notifications. 
  EasternWashington = "18", // All area covered by WSP districts 3, 4, 6. Mainly added for IRT users in those areas.

  // Possible future JOGs
  LewisCounty = "10",
  Spokane = "13",
  TriCities = "15",
  
  // Obsolete - Leaving commented to note ID has been used.
  // CowlitzCounty = "09",  // Combined Lower Columbia River JOG
  // ThurstonCounty = "14", // Not currently in progress.
  // SkagitCounty = "12",   // Replaced by NW WA JOG. 
  // WhatcomCounty = "16",  // Replaced by NW WA JOG.
}

export enum AgencyCode {
  NOT_SELECTED = "",
  OTHER = "Other",

  // Statewide / Multiple JOG
  WSP = "WSP",
  WSDOT = "WSDOT",
  NWSA = "NWSA", // Northwest Sea Port alliance
  KCM = "KCM",

  // SAJOG #################################  
  SDOT = "SDOT",
  SFD = "SFD",
  SPD = "SPD",
  PortOfSeattle = "Port",

  // NEJOG #################################
  CityOfBellevue = "COB",
  BellevueFire = "BellFD",
  BellevueDot = "BellDOT",
  BellevuePolice = "BellPD",
  BothellEmergencyMgmt = "BothEM",
  BothellPolice = "BothPD",
  EastsideFire = "EFR",
  FallCityFire = "FallCityFD",
  IssaquahFire = "IssaFD",
  IssaquahPublicWorks = "IssaPW",
  KingCountyEmergencyMgmt = "KingCoEM",
  KirklandFire = "KirkFD",
  KirklandPublicWorks = "KirkPW",
  MedinaPolice = "MDPD",
  MedinaPublicWorks = "MDPW",
  CityOfMercerIsland = "MI",
  MercerIslandPolice = "MIPD",
  CityOfNewcastle = "NewCastle",
  NewcastlePublicWorks = "NewCastlePW",
  Norcom = "NORCOM",
  RedmondFire = "RedFD",
  RedmondPolice = "RedPD",
  RedmondPublicWorks = "RedPW",
  ShorelineEmergencyMgmt = "ShoreEM",
  ShorelineFire = "ShoreFD",
  SnoqualmieFire = "SnoFD",
  ST = "ST",
  //SoundTransitKC Metro/Link LightRail
  WaEmergencyMgmtDiv = "WAEM",

  // South King County JOG (South JOG) ##########
  AuburnPolice = "AubPD",
  CityOfSeaTac = "CityOfSeatac",
  CityOfTukwila = "CityOfTukwila",
  CovingtonPolice = "CovPD",
  DesMoinesPublicWorks = "DesMoinesPW",
  DesMoinesPolice = "DesMoinesPD",
  FederalWayEmergencyMgmt = "FedWayEM",
  FederalWayPolice = "FedWayPD",
  FederalWayPublicWorks = "FedWayPW",
  KingCountyMedicOne = "KingCoMedOne",
  KentPolice = "KentPD",
  MapleValleyPolice = "MapValPD",
  MapleValleyPublicWorks = "MapValPW",
  MapleValleyEmergencyMgmt = "MapValEM",
  MountainViewFireAndRescue = "MtViewFireAndResue",
  PierceTransit = "PierceTransit",
  PugetSoundRegionalFireAuthority = "PugSoundFire",
  SeaTacEmergencyManagement = "SeaTacEM",
  SeaTacPublicWorks = "SeaTacPW",
  //Sound Transit
  //Sound Transit- KC Metro/Link Light Rail
  TukwilaEmergencyManagement = "TukEM",
  TukwilaPolice = "TukPD",
  TukwilaPublicWorks = "TukPW",
  ValleyRegionalFireAuthority = "ValRegFire",
  //WA Emergency Management Division

  // Tacoma JOG #################################
  BonneyLakeEmergencyManagementt = "BLEM",
  BonneyLakePublicWorks = "BLPW",
  BuckelyFire = "BuckleyFire",
  CentralPierceFireAndRescue = "CPFR",
  CityOfTacomaVenuesAndEvents = "TacVenEvents",
  EastPierceFireAndRescue = "EPFR",
  GigHarborFire = "GigFire",
  GigHarborPolice = "GigPD",
  LakewoodPolice = "LWPD",
  MiltonPublicWorks = "MiltonPW",
  // NWSA / Port of Tacoma/Port of Seattle
  OrtingEmergencyManagement = "OrtEM",
  OrtingPolice = "OrtPD",
  PierceCountyEmergencyMgmt = "PierceEM",
  PierceCountyPlanningAndPublicWorks = "PiercePW",
  PierceCountySheriffsOffice = "PierceSheriff",
  //PierceTransit = "PierceTransit", // Tacoma and South KC JOG
  PortOfTacoma = "POT",
  PuyallupEmergencyManagement = "PuyEM",
  PuyallupPolice = "PuyPD",
  PuyallupPublicWorks = "PuyPW",
  //Sound Transit
  SouthSound911 = "SouthSound911",
  SteilacoomPolice = "SteilPD",
  SumnerEmergencyManagement = "SumEM",
  SumnerPublicWorks = "SumPW",
  TacomaFireOperations = "TacFireOps",
  TacomaFireEmergencyMngmt = "TacFireEM",
  TacomaPublicWorks = "TacPW",
  TacomaRail = "TacRail",
  UniversityPlacePolice = "UPPD",
  //WA Emergency Management Division
  WestPierceFireAndRescue = "WPFR",
  WestPierceFireAndRescueEmergancyMgmt = "WPFREM",

  // Snohomish County JOG ######################
  CityOfGoldBar = "GLD",
  CityOfSultan = "SUL",
  CityOfLakeStevens = "LKSTV",
  CityOfMarysville = "MARY",

  ArlingtonPolice = "ArlPD",
  // BothellPolice = "BothPD", // Also in NEJOG
  BrierPublicWorks = "BrierPW",
  CommunityTransit = "CommTransit",
  EdmondsPoplice = "EdPD",
  EdmondsPublicWorks = "EdPW",
  EverettFire = "EvFD",
  EverettPolice = "EvPD",
  EverettPublicWorks = "EvPW",
  GoldBarPolice = "GBPD",
  GoldBarPublicWorks = "GBPW",
  LakeStevensPolice = "LSPD",
  LakeStevensPublicWorks = "LSPW",
  LynnwoodPublicWorks = "LynnPW",
  MarysvilleFire = "MaryFD",
  MarysvillePolice = "MARYPD",
  MarysvillePublicWorks = "MaryPW",
  MonroePolice = "MonPD",
  MountlakeTerracePublicWorks = "MLTPW",
  MukilteoPolice = "MukPD",
  NorthCountyRegionalFireAuthority = "NoCoFR",
  SnoCoEmergencyManagement = "SnoEM",
  SnohomishRegionalFireAndRescue = "SnoRegFR",
  SoundTransit = "ST",
  SouthCountyFire = "SoCoFire",
  StanwoodPublicWorks = "StanPW",
  StillaguamishPoplice = "StillPD",
  SultanPublicWorks = "SulPW",
  TulalipTribalPolice = "TulPD",
  //WA Emergency Management Division

  // Kitsap Peninsula JOG #######################
  BainbridgeIslandFire = "BIFD",
  BainbridgeIslandEmergencyManagement = "BIEM",
  BainbridgeIslandPolice = "BIPD",
  BremertonFire = "BIFD",
  BremertonPolice = "BremPD",
  BremertonPublicWorks = "BremPW",
  BrinnonFire = "BrinFD",
  CentralKitsapFireAndRescue = "CenKitFR",
  ClallamTransit = "ClAmTransit",
  EastJeffersonFireAndRescue = "EJFR",
  JeffersonCountyDeptOfEmergencyManagement = "JCEM",
  JeffersonCountySheriffsOffice = "JCSO",
  JeffersonTransitAuthority = "JTA",
  KitsapCountyDeptOfEmergencyManagement = "KitCoEM",
  KitsapCountySheriffsOffice = "KitCoSO",
  KitsapTransit = "KitTran",
  MACECOM911 = "MACECOM911",
  MasonCountyDeptOfEmergencyManagement = "MasCoEM",
  MasonCountyPublicWorks = "MasCoPW",
  MasonTransitAuthority = "MasTA",
  NavyRegionNorthwestFire = "NavyRegNWF",
  NavyShipyardEmergencyManagement = "NavySYEM",
  NorthKitsapFireAndRescue = "NKitFR",
  PortAngelesFire = "PAFD",
  PortGambleSKlallamTribeEmergencyManagement = "PGSTEM",
  PortOrchardPolice = "POPD",
  PortOrchardPublicWorks = "POPW",
  PortTownsendPolice = "PTPD",
  PoulsboEmergencyManagement = "PoulsEM",
  PoulsboFire = "PoulsFD",
  PoulsboPublicWorks = "PoulsPW",
  QuilceneFire = "QuilceneFD",
  SequimPolice = "SeqPD",
  SequimPublicWorks = "SeqPW",
  SheltonPublicWorks = "ShelPW",
  SkokomishTribalPolice = "SkoPD",
  SouthKitsapFireAndRescue = "SKitFR",

  // etc, etc
}

// Values that are saved in the dispatch event areaCode/ac field by dispatch processing
export enum AreaCode {
  I5 = "I5",
  WSP_D1 = "D1",
  WSP_D2 = "D2",
  WSP_D3 = "D3",
  WSP_D4 = "D4",
  WSP_D5 = "D5",
  WSP_D6 = "D6",
  WSP_D7 = "D7",
  WSP_D8 = "D8",
}

// Values that are saved in the dispatch event regionCode/rc field by dispatch processing
export enum RegionCode {
  NW = "NW",    // Northwest
  SEA = "SEA",  // King County,
  TAC = "TAC",  // Thurston, Peirce counties,
  SW = "SW",    // Southwest
  NC = "NC",    // North Central
  S = "S",      // South Central and Southeast
  E = "E",      // East
  PEN = "PEN",  // Olympic Peninsula
}
